export const SECTION_ANCHOR_OFFSET = 96;

export type SectionPosition = {
  id: string;
  top: number;
};

type OverflowStyleReader = (
  element: HTMLElement,
) => Pick<CSSStyleDeclaration, "overflowY">;

/**
 * Finds the element that owns vertical scrolling for a section navigation.
 * Most landing pages scroll with the window, while the tools directory keeps
 * its content in a dedicated overflow container.
 */
export const findScrollableAncestor = (
  element: HTMLElement | null,
  readStyle: OverflowStyleReader = (node) => window.getComputedStyle(node),
) => {
  let current = element?.parentElement ?? null;

  while (current) {
    const { overflowY } = readStyle(current);
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    ) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

/**
 * Selects the last section that has reached the navigation anchor line.
 * Keeping this calculation independent from the DOM makes the scroll boundary
 * explicit and protects it from drifting away from the sections' CSS offset.
 */
export const getActiveSectionId = (
  sections: readonly SectionPosition[],
  anchorOffset = SECTION_ANCHOR_OFFSET,
) => {
  if (sections.length === 0) return "";

  let activeId = sections[0].id;
  for (const section of sections) {
    if (section.top <= anchorOffset) {
      activeId = section.id;
    }
  }

  return activeId;
};
